"use client";

import { useCallback, useMemo } from "react";
import { mapSurfaceCardToHomeTask } from "@/components/agentic-surface/adapters";
import { HomeListTable } from "@/components/home/HomeListTable";
import { HOME_META_TEXT_CLASS } from "@/components/home/homeSurfaces";
import {
  HOME_SURFACE_TABLE_HEAD_BORDER,
  HomeSurfaceTableBody,
  HomeSurfaceTableCell,
  HomeSurfaceTableHead,
  HomeSurfaceTableHeaderCell,
  HomeSurfaceTableSortHeader,
} from "@/components/home/HomeSurfaceTable";
import { Button } from "@/components/ui/button/Button";
import { Badge } from "@/components/ui/badge/Badge";
import type { TaskSurfaceCard } from "@/services/api/agentic-v2/types";
import { useHomeSurfaceOptional } from "../../HomeSurfaceContext";
import { HomeNeedsYouRiskFilter } from "./HomeNeedsYouRiskFilter";
import { HomeTaskConsoleTableSkeleton } from "./HomeTaskConsoleTableSkeleton";
import {
  buildNeedsYouRows,
  formatWaiting,
  riskLabel,
  taskConsoleStatus,
  taskStatusLabel,
  type NeedsYouRiskFilter,
  type NeedsYouRiskTier,
  type NeedsYouSortColumn,
  type NeedsYouSortDirection,
} from "./needsYouTableModel";

function riskBadgeColor(
  risk: NeedsYouRiskTier,
): "statusDanger" | "statusWarning" | "statusInfo" {
  switch (risk) {
    case "high":
      return "statusDanger";
    case "medium":
      return "statusWarning";
    default:
      return "statusInfo";
  }
}

function statusBadgeColor(
  status: "needs_you" | "open" | "blocked" | "done",
): "amber" | "zinc" | "red" | "emerald" {
  switch (status) {
    case "needs_you":
      return "amber";
    case "blocked":
      return "red";
    case "done":
      return "emerald";
    default:
      return "zinc";
  }
}

export type HomeTaskConsoleTableProps = {
  tasks: TaskSurfaceCard[];
  loading?: boolean;
  serverBacked?: boolean;
  searchQuery?: string;
  onSearchQueryChange?: (value: string) => void;
  riskFilter?: NeedsYouRiskFilter;
  onRiskFilterChange?: (filter: NeedsYouRiskFilter) => void;
  sortColumn?: NeedsYouSortColumn;
  sortDirection?: NeedsYouSortDirection;
  onSortColumnChange?: (column: NeedsYouSortColumn) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** Overrides the default home-surface review opener (required outside a HomeSurfaceProvider). */
  onReviewTask?: (task: TaskSurfaceCard) => void;
  /**
   * First column content. Record pages (lead/client detail) show lifecycle
   * status instead of risk — their task payloads lack the risk enrichment
   * the home surface provides, and most rows don't need review.
   */
  firstColumn?: "risk" | "status";
  emptyMessage?: string;
  searchPlaceholder?: string;
  testId?: string;
};

export function HomeTaskConsoleTable({
  tasks,
  loading = false,
  serverBacked = false,
  searchQuery: controlledSearchQuery,
  onSearchQueryChange,
  riskFilter = "all",
  onRiskFilterChange,
  sortColumn = "risk",
  sortDirection = "desc",
  onSortColumnChange,
  hasMore = false,
  onLoadMore,
  onReviewTask,
  firstColumn = "risk",
  emptyMessage = "No tasks to show.",
  searchPlaceholder = "Search subtasks, runs, clients…",
  testId = "home-task-console-table",
}: HomeTaskConsoleTableProps) {
  const surface = useHomeSurfaceOptional();
  const rows = useMemo(() => buildNeedsYouRows(tasks), [tasks]);

  const handleReview = useCallback(
    (task: TaskSurfaceCard) => {
      if (onReviewTask) {
        onReviewTask(task);
        return;
      }
      const openHomeTask = surface?.openHomeTask;
      if (openHomeTask) {
        openHomeTask(mapSurfaceCardToHomeTask(task), { focus: "case" });
      }
    },
    [onReviewTask, surface],
  );
  const canReview = Boolean(onReviewTask ?? surface?.openHomeTask);

  if (loading && rows.length === 0) {
    return (
      <HomeTaskConsoleTableSkeleton
        testId={testId}
        showRiskFilter={Boolean(onRiskFilterChange)}
      />
    );
  }

  return (
    <>
      <HomeListTable
        testId={testId}
        searchQuery={controlledSearchQuery}
        onSearchQueryChange={onSearchQueryChange}
        searchPlaceholder={searchPlaceholder}
        toolbarTrailing={
          onRiskFilterChange ? (
            <HomeNeedsYouRiskFilter
              value={riskFilter}
              onChange={onRiskFilterChange}
              filteredCount={rows.length}
              testId={`${testId}-risk-filter`}
            />
          ) : null
        }
        loading={loading}
        empty={rows.length === 0}
        emptyMessage={emptyMessage}
      >
        <HomeSurfaceTableHead sticky>
          <tr className={HOME_SURFACE_TABLE_HEAD_BORDER}>
            <HomeSurfaceTableSortHeader
              label={firstColumn === "status" ? "Status" : "Risk"}
              column={firstColumn === "status" ? "status" : "risk"}
              activeColumn={sortColumn}
              direction={sortDirection}
              onSort={onSortColumnChange ?? (() => undefined)}
              testId={
                firstColumn === "status"
                  ? "home-task-console-sort-status"
                  : "home-task-console-sort-risk"
              }
            />
            <HomeSurfaceTableSortHeader
              label="Subtask"
              column="subtask"
              activeColumn={sortColumn}
              direction={sortDirection}
              onSort={onSortColumnChange ?? (() => undefined)}
              testId="home-task-console-sort-subtask"
            />
            <HomeSurfaceTableSortHeader
              label="Run · client"
              column="run_client"
              activeColumn={sortColumn}
              direction={sortDirection}
              onSort={onSortColumnChange ?? (() => undefined)}
              testId="home-task-console-sort-run_client"
            />
            <HomeSurfaceTableSortHeader
              label="Waiting"
              column="waiting"
              activeColumn={sortColumn}
              direction={sortDirection}
              onSort={onSortColumnChange ?? (() => undefined)}
              testId="home-task-console-sort-waiting"
            />
            <HomeSurfaceTableHeaderCell label="Action" align="right" />
          </tr>
        </HomeSurfaceTableHead>
        <HomeSurfaceTableBody>
          {rows.map((row) => (
            <tr key={row.task._id} data-testid={`${testId}-row-${row.task._id}`}>
              <HomeSurfaceTableCell variant="plain">
                {firstColumn === "status" ? (
                  <Badge color={statusBadgeColor(row.status)} size="sm">
                    {taskStatusLabel(row.status)}
                  </Badge>
                ) : (
                  <Badge color={riskBadgeColor(row.risk)} size="sm">
                    {riskLabel(row.risk)}
                  </Badge>
                )}
              </HomeSurfaceTableCell>
              <HomeSurfaceTableCell variant="title">{row.subtaskTitle}</HomeSurfaceTableCell>
              <HomeSurfaceTableCell variant="body">
                <p>{row.runTitle}</p>
                <p className={HOME_META_TEXT_CLASS}>{row.clientName}</p>
              </HomeSurfaceTableCell>
              <HomeSurfaceTableCell variant="meta">
                {formatWaiting(row.waitingMinutes)}
              </HomeSurfaceTableCell>
              <HomeSurfaceTableCell variant="plain" align="right">
                {canReview &&
                (firstColumn !== "status" ||
                  taskConsoleStatus(row.task) === "needs_you") ? (
                  <Button
                    size="sm"
                    outline
                    className="min-w-[5.5rem]"
                    onClick={() => handleReview(row.task)}
                    data-testid={`${testId}-review-${row.task._id}`}
                  >
                    Review
                  </Button>
                ) : null}
              </HomeSurfaceTableCell>
            </tr>
          ))}
        </HomeSurfaceTableBody>
      </HomeListTable>

      {serverBacked && hasMore ? (
        <div className="flex justify-center">
          <Button
            size="sm"
            plain
            onClick={onLoadMore}
            data-testid={`${testId}-load-more`}
          >
            Load more
          </Button>
        </div>
      ) : null}
    </>
  );
}
